import {
  faFileAlt,
  faPencilAlt,
  faTrashAlt
} from "@fortawesome/free-solid-svg-icons";
import { B2Button } from "@hdb/biome-2";
import { FC, createRef, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";
import { getUserInfo } from "../../../../../core/auth/auth.service";
import ModalComponent from "../../../../../core/components/modal/modal.component";
import TableComponent from "../../../../../core/components/table/table.component";
import HasPermission from "../../../../../core/guards/has-permission";
import SkeletonComponent from "../../../../../shared/custom-spinner/skeleton.component";
import FormBuilderComponent, {
  FormConfig,
} from "../../../../../shared/form-builder/form-builder.component";
import { getBlocksBySiteCode } from "../../../../../store/slices/location-block.slice";
import { getSiteByTCCode } from "../../../../../store/slices/location-site.slice";
import {
  addReportConfig,
  deleteReportConfig,
  editReportConfig,
  getReportConfigurations,
  getReportConfigurationsById,
  getReportGenerateTypes,
  getReportTypes,
} from "../../../../../store/slices/report-configuration.slice";
import { getTownCouncils } from "../../../../../store/slices/town-council.slice";
import {
  getAssociatedUsersByBlock,
  getuserbyemail,
} from "../../../../../store/slices/user.slice";
import SearchBarComponent from "../../search-bar/search-bar.component";

interface ReportConfigurationTableComponentProps {
  columns: any;
  data: any[];
  totalNumberOfRecords: number;
  pageSize: number;
  current: number;
  onChangePage: any;
  onChangeSort: any;
  onRefetchData: () => void;
  isLoading: boolean;
  onSearch: (val: string) => void;
}

const ReportConfigurationTableComponent: FC<
  ReportConfigurationTableComponentProps
> = ({
  columns,
  data,
  totalNumberOfRecords,
  pageSize,
  current,
  onChangePage,
  onChangeSort,
  onRefetchData,
  isLoading,
  onSearch,
}) => {
    const formRef = createRef();
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [isDetails, setIsDetails] = useState<boolean>(false);
    const [isDelete, setIsDelete] = useState<boolean>(false);
    const [townCouncilOptions, setTownCouncilOptions] = useState<any>([]);
    const [siteOptions, setSiteOptions] = useState<any>([]);
    const [blockOptions, setBlockOptions] = useState<any>([]);
    const [reportTypeOptions, setReportTypeOptions] = useState<any>([]);
    const [reportGenerateTypeOptions, setReportGenerateTypeOptions] =
      useState<any>([]);
    const [chartTypeOptions, setChartTypeOptions] = useState<any>([]);
    const [selectedBlockCode, setSelectedBlockCode] = useState<any>();
    const [selectedReportType, setSelectedReportType] = useState<any>(null);
    const [selectedIdToDeleteOrEdit, setSelectedIdToDeleteOrEdit] =
      useState<number>(0);
    const [currentUserId, setCurrentUserId] = useState(null);

    const user = getUserInfo();

    const [initialValues, setInitialValues] = useState<any>({
      reportName: "",
      tcCode: "",
      siteCode: "",
      blockCode: "",
      reportTypeId: "",
      generateTypeId: "",
      chartTypeId: null,
      schedulerDate: "",
      recipientIds: [currentUserId],
      email: user?.email?.toLowerCase(),
    });

    const dispatch = useDispatch();

    useEffect(() => {
      dispatch(getTownCouncils({ PageNumber: 0, pageSize: 0 }));
      dispatch(getReportTypes());
      dispatch(getReportGenerateTypes());
      dispatch(getuserbyemail());
    }, [dispatch]);

    const userbyEmail = useSelector((state: any) => {
      return state.user.userbyEmail;
    });

    useEffect(() => {
      if (Object.keys(userbyEmail)?.length) {
        setCurrentUserId(userbyEmail?.id);

        setInitialValues({
          reportName: "",
          tcCode: "",
          siteCode: "",
          blockCode: "",
          reportTypeId: "",
          generateTypeId: "",
          chartTypeId: null,
          schedulerDate: "",
          recipientIds: [userbyEmail?.id],
          email: user?.email?.toLowerCase(),
        });
      }
    }, [userbyEmail]);

    const townCouncilData = useSelector((state: any) => {
      return state.townCouncil.townCouncils;
    });

    const isLoadingTownCouncils = useSelector((state: any) => {
      return state.townCouncil.isLoadingTownCouncils;
    });

    const reportTypeData = useSelector((state: any) => {
      return state.reportConfiguration.reportTypes;
    });

    const isLoadingReportTypes = useSelector((state: any) => {
      return state.reportConfiguration.isLoadingReportTypes;
    });

    const reportGenerateTypeData = useSelector((state: any) => {
      return state.reportConfiguration.reportGenerateTypes;
    });

    const isLoadingReportGenerateTypes = useSelector((state: any) => {
      return state.reportConfiguration.isLoadingReportGenerateTypes;
    });

    const siteDataByTCCode = useSelector((state: any) => {
      return state.locationSite.sitesByTCCode;
    });

    /*     const isLocationSitesByTCCode = useSelector((state: any) => {
          return state.locationSite.isLocationSitesByTCCode;
        }); */

    const blockDataSiteCode = useSelector((state: any) => {
      return state.locationBlock.blocksBySiteCode;
    });

    /*     const isLoadingBlocksBySiteCode = useSelector((state: any) => {
          return state.locationBlock.isLoadingBlocksBySiteCode;
        }); */

    const isLoadingReportConfigById = useSelector((state: any) => {
      return state.reportConfiguration.isLoadingReportConfigById;
    });

    useEffect(() => {
      const tcs = townCouncilData?.data?.map((tc: any) => ({
        label: tc?.name,
        value: tc?.tcCode,
      }));

      setTownCouncilOptions(tcs);
    }, [townCouncilData]);

    useEffect(() => {
      const sites = siteDataByTCCode?.data?.map((site: any) => ({
        label: site?.name,
        value: site?.siteCode,
      }));

      setSiteOptions(sites);
    }, [siteDataByTCCode]);

    useEffect(() => {
      const blocks = blockDataSiteCode?.data?.map((block: any) => ({
        label: block?.blockName,
        value: block?.blockCode,
      }));

      setBlockOptions(blocks);
    }, [blockDataSiteCode]);

    useEffect(() => {
      const blocks = reportTypeData?.data?.map((block: any) => ({
        label: block?.displayName,
        value: block?.id,
      }));

      setReportTypeOptions(blocks);
    }, [reportTypeData]);

    useEffect(() => {
      const blocks = reportGenerateTypeData?.data?.map((block: any) => ({
        label: block?.name,
        value: block?.id,
      }));

      setReportGenerateTypeOptions(blocks);
    }, [reportGenerateTypeData]);

    const onChangeTC = (selectedTC: any) => {
      setSiteOptions([]);
      setBlockOptions([]);

      const form: any = formRef.current;

      if (form) {
        // Get all field names except 'towncouncil'
        const fieldNames = Object.keys(form.values).filter(
          (name) => name !== "towncouncil"
        );

        // Reset each field to empty, except 'towncouncil'
        fieldNames.forEach((fieldName) => {
          if (fieldName === "site" || fieldName === "block") {
            form.setFieldValue(fieldName, "", false); // false to avoid validation after setting field
          }
        });

        // console.log(form.values); // This will now reflect the reset operation
      }

      dispatch(getSiteByTCCode({ tcCode: selectedTC }));
    };

    const onChangeSite = (selectedSite: any) => {
      setBlockOptions([]);

      dispatch(getBlocksBySiteCode({ siteCode: selectedSite }));
    };

    const onChangeBlock = (selectedBlock: any) => {
      setSelectedBlockCode(selectedBlock);
    };

    const onChangeReport = (selectedReport: any) => {
      setSelectedReportType(selectedReport);
    };

    useEffect(() => {
      setChartTypeOptions([]);

      if (selectedReportType == "2") {
        const options = [
          { label: "Burn Time", value: 1 },
          { label: "Burn Time Percentage", value: 2 },
          // { label: "BurnTime_50K", value: 3 },
        ];

        setChartTypeOptions(options);
      } else if (selectedReportType == "3") {
        const options = [
          { label: "Motion Vs Time", value: 1 },
          { label: "Motion Vs SensorNode", value: 2 },
          { label: "Motion Vs Location", value: 3 },
        ];

        setChartTypeOptions(options);
      } else if (selectedReportType == "4") {
        const options = [
          { label: "Time Vs Sensor", value: 1 },
          { label: "Time Vs Location", value: 2 },
        ];

        setChartTypeOptions(options);
      } else if (selectedReportType == "5") {
        const options = [
          { label: "Enemy Consumption", value: 1 },
          { label: "Energy Saving", value: 2 },
          { label: "CO2 Saving", value: 3 },
          { label: "Financial Saving", value: 4 },
        ];

        setChartTypeOptions(options);
      } else {
        setChartTypeOptions([]);
      }
    }, [selectedReportType]);

    const reportConfigurationDetails = useSelector((state: any) => {
      return state.reportConfiguration.reportConfigurationDetails;
    });

    useEffect(() => {
      if (
        !reportConfigurationDetails ||
        !Object.keys(reportConfigurationDetails).length
      )
        return;

      setSelectedReportType(reportConfigurationDetails?.reportTypeId);

      setInitialValues({
        reportName: reportConfigurationDetails?.reportName || "",
        tcCode: reportConfigurationDetails?.tcCode || "",
        siteCode: reportConfigurationDetails?.siteCode || "",
        blockCode: reportConfigurationDetails?.blockCode || "",
        reportTypeId: reportConfigurationDetails?.reportTypeId || "",
        generateTypeId: reportConfigurationDetails?.generateTypeId || "",
        chartTypeId: reportConfigurationDetails?.chartTypeId || null,
        schedulerDate: reportConfigurationDetails?.schedulerDate || "",
        recipientIds: reportConfigurationDetails?.recipientIds || [currentUserId],
        email: reportConfigurationDetails?.recipientIds?.length
          ? null
          : user?.email?.toLowerCase(),
      });

      setSelectedBlockCode(reportConfigurationDetails?.blockCode);
    }, [reportConfigurationDetails]);

    const handleCloseModal = () => {
      setIsModalOpen(false);
      setIsEdit(false);
      setIsDetails(false);
      setIsDelete(false);
      setChartTypeOptions([]);
      setSelectedReportType(null);
      setSelectedBlockCode(null);
      setSiteOptions([]);
      setBlockOptions([]);

      setInitialValues({
        reportName: "",
        tcCode: "",
        siteCode: "",
        blockCode: "",
        reportTypeId: "",
        generateTypeId: "",
        chartTypeId: null,
        schedulerDate: "",
        recipientIds: [currentUserId],
        email: user?.email?.toLowerCase(),
      });
    };

    const editAction = (id: number) => {
      setIsEdit(true);
      setIsModalOpen(true);
      setIsDetails(false);
      setIsDelete(false);

      dispatch(getReportConfigurationsById({ reportConfigurationId: id }))
        .then((data: any) => {
          Promise.all([
            dispatch(getSiteByTCCode({ tcCode: data?.payload?.tcCode })),
            dispatch(getBlocksBySiteCode({ siteCode: data?.payload?.siteCode })),
          ]).then(() => { });
        })
        .catch((error: any) => {
          console.log("Error in api call: ", error);
        });

      setSelectedIdToDeleteOrEdit(id);
    };

    const detailsAction = (id: number) => {
      setIsEdit(false);
      setIsModalOpen(true);
      setIsDetails(true);
      setIsDelete(false);

      dispatch(getReportConfigurationsById({ reportConfigurationId: id }))
        .then((data: any) => {
          Promise.all([
            dispatch(getSiteByTCCode({ tcCode: data?.payload?.tcCode })),
            dispatch(getBlocksBySiteCode({ siteCode: data?.payload?.siteCode })),
          ]).then(() => { });
        })
        .catch((error: any) => {
          console.log("Error in api call: ", error);
        });

      setSelectedIdToDeleteOrEdit(id);
    };

    const deleteAction = (id: number) => {
      setIsDetails(false);
      setIsModalOpen(true);
      setIsEdit(false);
      setIsDelete(true);

      setSelectedIdToDeleteOrEdit(id);
    };

    const handleDelete = () => {
      dispatch(
        deleteReportConfig({ reportConfigurationId: selectedIdToDeleteOrEdit })
      ).then(() => {
        onRefetchData();
        handleCloseModal();
      });
    };

    const onSubmit = () => {
      const form: any = formRef.current;

      if (form) {
        form.handleSubmit();

        if (form.chartTypeId == "") {
          form.chartTypeId = null;
        }

        const {
          reportName,
          tcCode,
          siteCode,
          blockCode,
          reportTypeId,
          generateTypeId,
          chartTypeId,
        } = form?.values || {};

        if (
          !reportName ||
          !tcCode ||
          !siteCode ||
          !blockCode ||
          !reportTypeId ||
          !generateTypeId
        )
          return;

        if (
          (reportTypeId == 2 ||
            reportTypeId == 3 ||
            reportTypeId == 4 ||
            reportTypeId == 5) &&
          !chartTypeId
        )
          return;

        if (isEdit) {
          const formValues = form.values;
          const payload = {
            ...formValues,
            reportConfigurationId: selectedIdToDeleteOrEdit,
          };

          dispatch(
            editReportConfig({
              payload,
              reportConfigurationId: selectedIdToDeleteOrEdit,
            })
          ).then(() => {
            handleCloseModal();
            onRefetchData();
          });
        } else {
          dispatch(addReportConfig({ payload: form.values })).then(() => {
            handleCloseModal();
            onRefetchData();
          });
        }
      }
    };

    let modalActionConfig: any = [];

    if (!isDetails) {
      modalActionConfig = isDelete
        ? [
          {
            label: "Yes",
            event: handleDelete,
            variant: "primary",
          },
        ]
        : [
          {
            label: "Save",
            event: onSubmit,
            variant: "primary",
          },
        ];
    }

    const actionConfig: any = [
      {
        label: "Edit",
        actionProperty: "id",
        event: editAction,
        variant: "secondary",
        className: "mx-2 my-1",
        moduleName: "Report Configuration",
        actionName: "Edit-Report Configuration",
        icon: faPencilAlt,
      },
      {
        label: "Details",
        actionProperty: "id",
        event: detailsAction,
        variant: "info",
        className: "mx-2 my-1",
        moduleName: "Report Configuration",
        actionName: "View-Report Configuration",
        icon: faFileAlt,
      },
      {
        label: "Delete",
        actionProperty: "id",
        event: deleteAction,
        variant: "danger",
        className: "mx-2 my-1",
        moduleName: "Report Configuration",
        actionName: "Delete-Report Configuration",
        icon: faTrashAlt,
      },
    ];

    const emptyConfig = {
      title: "No Report Configuration Found",
      description: "",
    };

    const [formConfiguration, setFormConfiguration] = useState<FormConfig>({
      controls: [],
    });

    useEffect(() => {
      const controls = [
        {
          name: "reportName",
          label: "Report Name",
          placeholder: "Weekly Energy Report",
          type: "text",
          required: true,
          readOnly: isDetails,
          disabled: isDetails,
        },
        {
          name: "tcCode",
          label: "Town Council",
          placeholder: "Select Town Council",
          type: "select",
          options: townCouncilOptions,
          onChange: onChangeTC,
          required: true,
          readOnly: isDetails,
          disabled: isDetails,
        },
        {
          name: "siteCode",
          label: "Site",
          placeholder: "Select Site",
          type: "select",
          options: siteOptions,
          required: true,
          onChange: onChangeSite,
          readOnly: isDetails,
          disabled: isDetails,
        },
        {
          name: "blockCode",
          label: "Block",
          placeholder: "Select Block",
          type: "select",
          options: blockOptions,
          onChange: onChangeBlock,
          required: true,
          readOnly: isDetails,
          disabled: isDetails,
        },
        {
          name: "reportTypeId",
          label: "Report Type",
          placeholder: "Select Report Type",
          type: "select",
          options: reportTypeOptions,
          onChange: onChangeReport,
          required: true,
          readOnly: isDetails,
          disabled: isDetails,
        },
      ];

      if (
        chartTypeOptions?.length > 0 ||
        selectedReportType == 2 ||
        selectedReportType == 3 ||
        selectedReportType == 4 ||
        selectedReportType == 5
      ) {
        const newItems = [
          {
            name: "chartTypeId",
            label: "Chart Type",
            placeholder: "Select chart type",
            type: "select",
            options: chartTypeOptions,
            required: true,
            readOnly: isDetails,
            disabled: isDetails,
          },
          {
            name: "generateTypeId",
            label: "Automatic Generate Report",
            placeholder: "Select Report Type",
            type: "select",
            options: reportGenerateTypeOptions,
            required: true,
            readOnly: isDetails,
            disabled: isDetails,
          },
          {
            name: "recipientIds",
            label: "Add Recepients",
            placeholder: "",
            type: "chips",
            required: true,
            disabled: isDetails,
            dispatchableApi: {
              api: getAssociatedUsersByBlock,
              params: { blockCode: selectedBlockCode },
              selectableProperty: "email",
            },
          },
        ];

        const newControls = [...controls, ...newItems];

        setFormConfiguration({ controls: newControls });
      } else {
        const newItems = [
          {
            name: "generateTypeId",
            label: "Automatic Generate Report",
            placeholder: "Select Report Type",
            type: "select",
            options: reportGenerateTypeOptions,
            required: true,
            readOnly: isDetails,
            disabled: isDetails,
          },
          {
            name: "recipientIds",
            label: "Add Recepients",
            placeholder: "",
            type: "chips",
            required: true,
            disabled: isDetails,
            dispatchableApi: {
              api: getAssociatedUsersByBlock,
              params: { blockCode: selectedBlockCode },
              selectableProperty: "email",
            },
          },
        ];

        const newControls = [...controls, ...newItems];

        setFormConfiguration({ controls: newControls });
      }
    }, [
      chartTypeOptions,
      townCouncilOptions,
      siteOptions,
      blockOptions,
      selectedReportType,
      selectedBlockCode,
    ]);

    const validationSchema = yup.object().shape({
      reportName: yup.string().trim().required("Report Name is required"),
      tcCode: yup.string().required("Town Council is required"),
      siteCode: yup.string().required("Site is required"),
      blockCode: yup.string().required("Block is required"),
      reportTypeId: yup.string().required("Report Type is required"),
      generateTypeId: yup
        .string()
        .required("Automatic Generate Report is required"),
      recipientIds: yup
        .array()
        .test(
          "validation",
          "Minimum one Recipient is required",
          (_value, context) => {
            const { recipientIds } = context.parent;

            if (recipientIds?.length > 0) return true;
            return false;
          },
        ),
      chartTypeId: yup
        .string()
        .test("test", "Chart type is required", function (value, context) {
          const { reportTypeId } = context.parent;
          if (
            reportTypeId == 2 ||
            reportTypeId == 3 ||
            reportTypeId == 4 ||
            reportTypeId == 5
          ) {
            return !!value;
          }
          return true;
        }),
    });

    let modalHeaderTitle = "Add Report Configuration";
    if (isDelete) {
      modalHeaderTitle = "Delete Report Configuration";
    } else if (isDetails) {
      modalHeaderTitle = "Report Configuration Details";
    } else if (isEdit) {
      modalHeaderTitle = "Edit Report Configuration";
    }

    return (
      <div>
        <div>
          <div className="d-flex gap-3 justify-content-end table-generic-action">
            <SearchBarComponent
              placeholder="Search Report Configuration"
              methodToDispatch={getReportConfigurations}
              getSearch={onSearch}
            />
            <HasPermission
              moduleName="Report Configuration"
              actionName="Add-Report Configuration"
              isRoute={false}
            >
              <B2Button
                className="icon-text-btn"
                variant="primary"
                onClick={() => setIsModalOpen(true)}
              >
                {/* <FontAwesomeIcon icon={faCirclePlus} className="fs-7 me-2" /> */}
                Add Configuration
              </B2Button>
            </HasPermission>
          </div>
          <div className="my-3">
            <TableComponent
              tableHeaders={columns}
              emptyConfig={emptyConfig}
              data={data}
              totalNumberOfRecords={totalNumberOfRecords}
              pageSize={pageSize}
              current={current}
              onChangePage={onChangePage}
              actionConfig={actionConfig}
              onChangeSort={onChangeSort}
              isLoadingData={isLoading}
            />
          </div>
        </div>

        {/* modals */}
        {isModalOpen && (
          <ModalComponent
            isConformationModal={isDelete}
            showModal={isModalOpen}
            handleClose={handleCloseModal}
            headerTitle={modalHeaderTitle}
            actionConfig={modalActionConfig}
          >
            {isDelete ? (
              <h5 className="text-center">
                Are you sure to <b className="text-danger">Delete</b>?
              </h5>
            ) : (
              <SkeletonComponent
                wrapperHeight="380px"
                skeletonHeight="50px"
                isLoading={
                  isEdit || isDetails
                    ? isLoadingReportGenerateTypes ||
                    isLoadingReportTypes ||
                    isLoadingTownCouncils ||
                    isLoadingReportConfigById
                    : false
                }
                count={6}
              >
                <FormBuilderComponent
                  formRef={formRef}
                  initialValues={initialValues}
                  validation={validationSchema}
                  formConfig={formConfiguration}
                />
              </SkeletonComponent>
            )}
          </ModalComponent>
        )}
      </div>
    );
  };

export default ReportConfigurationTableComponent;
